import { LogOut, MoreHorizontal, UserCog } from "lucide-react";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Avatar, StatusDot } from "@/components/ui";
import { useClickOutside } from "@/lib/useClickOutside";
import { useSession } from "@/lib/session";
import { ThemeToggle } from "./ThemeToggle";

/**
 * Bottom-of-sidebar agent identity: avatar + name + a green "Active" presence
 * dot, with an overflow menu holding the theme toggle and sign-out.
 */
export function AgentFooter() {
  const { t } = useTranslation();
  const { me, logout } = useSession();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useClickOutside<HTMLDivElement>(open, useCallback(() => setOpen(false), []));

  const user = me?.user ?? null;

  if (!user) return null;

  const displayName = user.name || user.email;

  return (
    <div ref={ref} className="relative border-t border-hairline p-2">
      <div className="flex items-center gap-2.5 rounded-md p-1.5">
        <Avatar name={user.name} email={user.email} size={32} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-body font-semibold text-ink">{displayName}</p>
          <p className="flex items-center gap-1.5 text-meta text-muted">
            <StatusDot tone="success" size={7} />
            {t("common.active")}
          </p>
        </div>
        <button
          type="button"
          aria-label={t("common.settings")}
          onClick={() => setOpen((v) => !v)}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted transition-colors hover:bg-hairline hover:text-ink"
        >
          <MoreHorizontal size={16} strokeWidth={2} />
        </button>
      </div>

      {open ? (
        <div className="absolute bottom-full left-2 right-2 z-20 mb-1 flex flex-col gap-0.5 rounded-md border border-border bg-bg p-1 shadow-window">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate("/account");
            }}
            className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-body transition-colors hover:bg-hairline"
          >
            <UserCog size={16} strokeWidth={2} />
            {t("account.title")}
          </button>
          <ThemeToggle />
          <button
            type="button"
            onClick={() => void logout()}
            className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-body text-body transition-colors hover:bg-hairline"
          >
            <LogOut size={16} strokeWidth={2} />
            {t("common.signOut")}
          </button>
        </div>
      ) : null}
    </div>
  );
}
